import { Link } from "react-router-dom";
import { Camera, Ruler, ShieldCheck, Waves, Trophy, ArrowRight } from "lucide-react";
import { HERO_IMG } from "../constants/states";
import { ShareButton } from "../components/ShareButton";
import { Logo } from "../components/Logo";

const TICKER = ["Redfish", "Snook", "Largemouth Bass", "Striped Bass", "Speckled Trout", "Flounder", "Walleye", "Rainbow Trout", "Tarpon", "Crappie"];

const FEATURES = [
  { icon: Camera, title: "Snap it", body: "Point your phone at the fish. We name the species in seconds." },
  { icon: Ruler, title: "Size it", body: "Get a length estimate and whether it's a legal keeper in your state." },
  { icon: Waves, title: "Time it", body: "Bite ratings from tide, wind, pressure and light for every spot you save." },
  { icon: Trophy, title: "Rank it", body: "Log every fish and climb the public leaderboard — first names only." },
];

export default function Landing() {
  return (
    <div className="min-h-screen">
      <section className="relative min-h-[92vh] flex flex-col grain overflow-hidden border-b-2 border-[#FF4D00]">
        <img src={HERO_IMG} alt="Angler casting into the surf at golden hour" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-black/40 to-black/30" />
        <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 h-16">
<span data-testid="landing-logo"><Logo size={36} /></span>
          <Link to="/login" className="text-sm font-bold uppercase tracking-wider border-b-2 border-[#FF4D00]" data-testid="landing-login-link">Log in</Link>
        </header>
        <div className="relative z-10 flex-1 flex flex-col justify-end px-5 sm:px-10 pb-14 max-w-4xl">
          <span className="rise text-xs font-bold uppercase tracking-[0.3em] text-[#FF4D00]">Free for every angler</span>
          <h1 className="rise font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-4" style={{ animationDelay: ".08s" }}>
            What did<br />you just<br /><span className="text-[#FF4D00]">catch?</span>
          </h1>
          <p className="rise mt-6 max-w-lg text-sm sm:text-base text-zinc-200" style={{ animationDelay: ".16s" }}>
            Scan the fish. Know the species, if it's good to eat, how long it is and whether you can keep it.
          </p>
          <div className="rise mt-8 flex flex-wrap gap-4" style={{ animationDelay: ".24s" }}>
            <Link to="/signup" className="brut-btn" data-testid="landing-signup-btn">Start scanning <ArrowRight size={18} /></Link>
            <ShareButton className="!bg-transparent !text-white !border-white" />
          </div>
        </div>
      </section>

      <div className="overflow-hidden bg-[#FF4D00] text-black border-b-2 border-black py-3">
        <div className="marquee flex w-max gap-10 font-display uppercase text-lg whitespace-nowrap">
          {[...TICKER, ...TICKER].map((t, i) => <span key={i}>{t} ✕</span>)}
        </div>
      </div>

      <section className="px-5 sm:px-10 py-20 max-w-5xl">
        <h2 className="brut-h2">Built for the water's edge</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {FEATURES.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className={`p-6 ${i === 0 ? "brut-card-hot" : "brut-card"}`} data-testid={`landing-feature-${i}`}>
              <Icon className="text-[#FF4D00]" size={28} strokeWidth={2.5} />
              <h3 className="font-display uppercase text-xl mt-4">{title}</h3>
              <p className="mt-2 text-sm text-zinc-400">{body}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex items-center gap-3 text-xs text-zinc-500">
          <ShieldCheck size={16} /> Keeper sizes are estimates only — always check your state's official rules.
        </div>
        <Link to="/signup" className="brut-btn mt-10" data-testid="landing-bottom-signup-btn">Create free account</Link>
      </section>
    </div>
  );
}
